import { MatchAction } from './MatchAction';
import { Draw, newDraw, TeamArmy } from '@Models';
import { Result } from '@Redux/type';
import { container } from '@Container';
import { TYPES } from '@Types';
import {
  DrawRepository,
  RequestBooleanSuccess,
  RequestDrawRoundSuccess,
  RequestDrawSuccess,
} from '@Repository/tool/pairing';
import { inject, injectable } from 'inversify';
import matchesSlice from '@Redux/reducers/tool/pairing/matches.slice';
import opponentDrawSlice from '@Redux/reducers/tool/pairing/opponentDraw.slice';
import myDrawSlice from '@Redux/reducers/tool/pairing/myDraw.slice';
import { ICrudEntityAction } from '@Redux/ICrudEntityAction';
import { PayloadAction } from '@reduxjs/toolkit';

@injectable()
export class MyDrawAction extends ICrudEntityAction<Draw> {
  protected fetchDataSuccess(item: Draw): PayloadAction<Draw> {
    return myDrawSlice.actions.setDraw(item);
  }
  protected cacheDataSuccess(item: Draw): PayloadAction<Draw> {
    return myDrawSlice.actions.setDraw(item);
  }
  protected haveError(message: string): PayloadAction<string> {
    return myDrawSlice.actions.setError(message);
  }
  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return myDrawSlice.actions.setLoading(bool);
  }
  @inject(TYPES.Repository.DrawRepository) private _repository!: DrawRepository;

  public loadDrawRequest =
    (teamId: string): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.getDrawRound({
        draftTeamId: teamId,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestDrawRoundSuccess;

      dispatch(matchesSlice.actions.setMatches(success.payload.matches));

      const closedLocaly =
        (getState().myDraw.entity?.selectedTeamArmyConfirmed ?? false) &&
        (getState().opponentDraw.entity?.selectedTeamArmyConfirmed ?? false);

      if (
        closedLocaly &&
        !success.payload.myDrawExist &&
        !success.payload.opponentDrawExist
      ) {
        dispatch(myDrawSlice.actions.closeOrReset());
        dispatch(opponentDrawSlice.actions.closeOrReset());
      } else {
        if (success.payload.myDrawExist) {
          dispatch(
            myDrawSlice.actions.poolUpdateSuccess(success.payload.myDraw!!),
          );
        } else {
          dispatch(myDrawSlice.actions.closeOrReset());
        }
        if (success.payload.opponentDrawExist) {
          dispatch(
            opponentDrawSlice.actions.poolUpdateSuccess(
              success.payload.opponentDraw!!,
            ),
          );
        } else {
          dispatch(opponentDrawSlice.actions.closeOrReset());
        }
      }
      dispatch(this.areLoading(false));
    };

  public selectArmyToDrawRequest =
    (draw: Draw): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.createDraw({
        draw: draw,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestDrawSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.loadDrawRequest(draw.teamId));
      dispatch(this.areLoading(false));
    };

  public confirmArmyToDraw =
    (draw: Draw): Result<void> =>
    async (dispatch, getState) => {
      const state = getState();
      dispatch(
        this.selectArmyToPropositionRequest(state.myTeam.entity!.id, draw),
      );
    };

  public selectArmyToPropositionRequest =
    (teamId: string, draw: Draw): Result<void> =>
    async (dispatch) => {
      dispatch(opponentDrawSlice.actions.setLoading(true));

      const response = await this._repository.updateDraw({
        draw: draw,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestDrawSuccess;
      dispatch(opponentDrawSlice.actions.setDraw(success.payload));
      dispatch(this.loadDrawRequest(teamId));
      dispatch(opponentDrawSlice.actions.setLoading(false));
    };

  public selectPropositionRequest =
    (): Result<void> => async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const matchesAction = container.get<MatchAction>(
        TYPES.Actions.Tool.MatchAction,
      );

      const state = getState();
      const draftId = state.draft.id!;
      const draw: Draw = state.myDraw.entity!;

      const response = await this._repository.selectArmyInDraw({
        draw: draw,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestBooleanSuccess;
      dispatch(myDrawSlice.actions.selectSuccess(draw.myTeamArmy));
      if (success.payload) {
        dispatch(myDrawSlice.actions.closeOrReset());
        dispatch(opponentDrawSlice.actions.closeOrReset());
        dispatch(matchesAction.getMatches(draftId as string));
      }
      dispatch(this.areLoading(false));
    };

  public selectArmy =
    (teamArmy: TeamArmy): Result<void> =>
    async (dispatch, getState) => {
      const state = getState();

      const myDraw = state.myDraw.entity;
      const opponentDraw = state.opponentDraw.entity;

      if (!myDraw) {
        dispatch(
          myDrawSlice.actions.poolUpdateSuccess(
            newDraw(state.myTeam.entity!.id, teamArmy),
          ),
        );
      } else if (!myDraw.myTeamArmyConfirmed) {
        dispatch(myDrawSlice.actions.selectArmy(teamArmy));
      } else if (opponentDraw?.myTeamArmyConfirmed) {
        if (!opponentDraw.propositionTeamArmyOneId) {
          dispatch(opponentDrawSlice.actions.selectPropositionOne(teamArmy));
        } else if (!opponentDraw.propositionTeamArmyTwoId) {
          dispatch(opponentDrawSlice.actions.selectPropositionTwo(teamArmy));
        }
      }
    };
}
